import { isAxiosError } from "axios";

import { api } from "@/lib/api/axios";
import {
  toRegionalSpelling,
  type RegionId,
  type TciInputs,
  type TciResult,
  type WorkflowId,
} from "@/lib/tci";

export type CreateTciCalculationPayload = {
  /** Anonymous visitor session to convert on unlock */
  sessionId?: string;
  email: string;
  consent: boolean;
  workflowId: WorkflowId;
  regionId: RegionId | null;
  businessSector: string | null;
  organisationName: string | null;
  viewMode?: "presenter" | "visitor";
  laborHeadcount: number;
  laborTimePercent: number;
  laborSalary: number;
  yieldVolume: number;
  yieldPenaltyPerEvent: number;
  untappedVolume: number;
  untappedActionablePercent: number;
  untappedValuePerAction: number;
  annualEbitda: number;
  laborWaste: number;
  yieldPenaltyWaste: number;
  untappedValue: number;
  totalTrapped: number;
  tciScore: number | null;
  bandId: string | null;
  bandLabel: string | null;
};

export type CreateTciCalculationResponse = {
  id: string;
};

export function buildCreateTciCalculationPayload(args: {
  email: string;
  consent: boolean;
  regionId: RegionId | null;
  businessSector: string | null;
  organisationName: string | null;
  inputs: TciInputs;
  result: TciResult;
  sessionId?: string | null;
  viewMode?: "presenter" | "visitor";
}): CreateTciCalculationPayload {
  const {
    email,
    consent,
    regionId,
    businessSector,
    organisationName,
    inputs,
    result,
    sessionId,
    viewMode,
  } = args;

  return {
    ...(sessionId ? { sessionId } : {}),
    ...(viewMode ? { viewMode } : {}),
    email: email.trim(),
    consent,
    workflowId: inputs.workflowId,
    regionId,
    businessSector,
    organisationName: organisationName?.trim() || null,
    laborHeadcount: inputs.labor.headcount,
    laborTimePercent: inputs.labor.timePercent,
    laborSalary: inputs.labor.salary,
    yieldVolume: inputs.yieldPenalty.volume,
    yieldPenaltyPerEvent: inputs.yieldPenalty.penaltyPerEvent,
    untappedVolume: inputs.untapped.volume,
    untappedActionablePercent: inputs.untapped.actionablePercent,
    untappedValuePerAction: inputs.untapped.valuePerAction,
    annualEbitda: inputs.annualEbitda,
    laborWaste: result.laborWaste,
    yieldPenaltyWaste: result.yieldPenalty,
    untappedValue: result.untappedValue,
    totalTrapped: result.totalTrapped,
    tciScore: result.tciScore,
    bandId: result.band?.id ?? null,
    bandLabel: result.band
      ? toRegionalSpelling(result.band.label, regionId)
      : null,
  };
}

export async function createTciCalculation(
  payload: CreateTciCalculationPayload,
): Promise<CreateTciCalculationResponse> {
  const { data } = await api.post<CreateTciCalculationResponse>(
    "/tci-calculations",
    payload,
  );
  return data;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isAxiosError(error)) return fallback;

  const data = error.response?.data as
    | { message?: string | string[] }
    | undefined;
  const message = data?.message;

  if (Array.isArray(message) && message.length > 0) {
    return message.join(", ");
  }
  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return fallback;
}
